import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { alpha } from "@mui/material/styles"
import Badge from "@mui/material/Badge"
import Popover from "@mui/material/Popover"
import Box from "@mui/material/Box"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import Divider from "@mui/material/Divider"
import Tooltip from "@mui/material/Tooltip"

import {
  invalidateCredentials,
  selectCurrentUser,
  selectCode,
} from "features/store/authSlice"
import { PREVIOUS_LOCATION_KEY } from "helpers/utils/constants"
import { WarningPassword } from "types/password.types"

import Avatar from "./Avatar"
// import ThemeSwitch from "./ThemeSwitch"
import ChangePasswordModal from "./ChangePasswordModal"
import PasswordAlert from "./PasswordAlert"
import LanguageSelect from "./LanguageSelect"

const menuItemStyles = {
  typography: "body2",
  py: 1,
  px: 2.5,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}

const nonClickableMenuItemStyles = {
  ...menuItemStyles,
  cursor: "default",
  "&:hover": {
    backgroundColor: "transparent",
  },
}

export default function ProfileMenu() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(selectCurrentUser)
  const code = useSelector(selectCode)

  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [showWarningAlert, setShowWarningAlert] = useState(false)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleLogout = () => {
    dispatch(invalidateCredentials())
    sessionStorage.removeItem(PREVIOUS_LOCATION_KEY)
    navigate("/accounts/login")
  }

  useEffect(() => {
    const showAlert = code ? Object.keys(WarningPassword).includes(code) : false
    setShowWarningAlert(showAlert)
  }, [code])

  return (
    <>
      <Tooltip title={t("account_settings.PROFILE")}>
        <IconButton
          ref={anchorRef}
          onClick={handleOpen}
          sx={{
            padding: 0,
            mr: 2,
            width: 44,
            height: 44,
            ...(open && {
              bgcolor: (theme) =>
                alpha(theme.palette.primary.main, theme.palette.action.focusOpacity),
            }),
          }}
        >
          <Badge
            color="warning"
            variant="dot"
            overlap="circular"
            invisible={!showWarningAlert}
          >
            <Avatar email={user} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              ml: 0.5,
              overflow: "inherit",
              width: 240,
            },
          },
        }}
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="body2" noWrap>
            {user}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        {/* <MenuItem disableRipple sx={nonClickableMenuItemStyles}>
          {t("generic.DARK_MODE")}
          <ThemeSwitch />
        </MenuItem> */}
        <MenuItem disableRipple sx={nonClickableMenuItemStyles}>
          {t("generic.LANGUAGE")}
          <LanguageSelect />
        </MenuItem>
        <MenuItem onClick={() => setShowChangePasswordModal(true)} sx={menuItemStyles}>
          {t("generic.CHANGE_PASSWORD")}
        </MenuItem>
        {showWarningAlert && <PasswordAlert passwordCode={code} />}
        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button fullWidth color="inherit" variant="outlined" onClick={handleLogout}>
            {t("generic.LOGOUT")}
          </Button>
        </Box>
      </Popover>
      <ChangePasswordModal
        open={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </>
  )
}
